import { Quality, GameVersion } from 'common';
import Data from 'data';
import { itemAffixLevel, itemGetTypes, modsToStats, runewordStats, statsToLines, uniqueStats } from "./item";

// t[P][S]
const magicProb = [[0, 2], [1, 1]]; // out of 4
const rareProb = [[0, 0, 0, 2], [0, 0, 6, 5], [0, 6, 6, 8], [2, 6, 8, 16]]; // out of 64
const craftProb30 = [[ 0, 16,  4,  2], [16,  8,  6,  5], [ 4,  6,  6,  0], [ 2,  5,  0,  0]]; // out of 80
const craftProb50 = [[ 0,  0, 12,  2], [ 0, 24,  6,  5], [12,  6,  6,  0], [ 2,  5,  0,  0]]; // out of 80
const craftProb70 = [[ 0,  0,  0,  8], [ 0,  0, 24,  5], [ 0, 24,  6,  0], [ 8,  5,  0,  0]]; // out of 80
const craftProb99 = [[ 0,  0,  0,  0], [ 0,  0,  0, 25], [ 0,  0, 30,  0], [ 0, 25,  0,  0]]; // out of 80

function processGroups(groups, alvl) {
  const grouped = {};
  for (const g of groups) {
    const out = [];
    let prob = 0;
    for (const id of g.mods) {
      const mod = Data.mods[id];
      if (mod.level && mod.level > alvl) continue;
      if (mod.maxlevel && mod.maxlevel < alvl) continue;
      out.push(id);
      prob += (mod.frequency == null ? 1 : mod.frequency);
    }
    if (!grouped[g.group]) {
      grouped[g.group] = {groups: [], ids: new Set(), frequency: 0};
    }
    grouped[g.group].groups.push({mods: out, frequency: prob});
    for (const id of out) grouped[g.group].ids.add(id);
    grouped[g.group].frequency += prob;
  }
  return Object.values(grouped);
}

function pickFrequency(weights, indices, maxCount) {
  const sum = weights.reduce((sum, x) => sum + x, 0);
  const freq = [indices.length ? 0 : 1, ...[...Array(maxCount)].map(() => 0)];
  const total = [1, ...[...Array(maxCount)].map(() => 0)];

  if (indices.length <= maxCount) {
    for (let m0 = 0; m0 < weights.length; ++m0) {
      const f0 = weights[m0];
      const i0 = indices.indexOf(m0);
      let r0, r1, r2;
      if (i0 >= 0) {
        r0 = indices[i0 > 0 ? 0 : 1];
        r1 = indices[i0 > 1 ? 1 : 2];
        r2 = null;
      } else {
        r0 = indices[0];
        r1 = indices[1];
        r2 = indices[2];
      }
      if (r0 == null) freq[1] += f0;
      total[1] += f0;

      if (maxCount <= 1) continue;

      for (let m1 = 0; m1 < weights.length; ++m1) {
        if (m1 === m0) continue;

        const f1 = weights[m1] * f0;
        total[2] += f1;
        if (maxCount >= 3) total[3] += f1 * (sum - f0 - weights[m1]);

        if (r2) continue;

        let r3;
        if (m1 === r0 || m1 === r1) {
          r3 = (m1 === r0 ? r1 : r0);
        } else {
          if (r1 != null) continue;
          r3 = r0;
        }

        if (r3 == null) freq[2] += f1;

        if (maxCount >= 3) {
          if (r3 == null) {
            freq[3] += f1 * (sum - f0 - weights[m1]);
          } else {
            freq[3] += f1 * weights[r3];
          }
        }
      }
    }
  }

  if (maxCount >= 1) freq[1] /= total[1];
  if (maxCount >= 2) freq[2] /= total[2];
  if (maxCount >= 3) freq[3] /= total[3];

  return freq;
}

function probTable(quality, ilvl) {
  if (quality === Quality.MAGIC) return [magicProb, 4];
  if (quality === Quality.RARE) return [rareProb, 64];
  if (ilvl <= 30) return [craftProb30, 80];
  if (ilvl <= 50) return [craftProb50, 80];
  if (ilvl <= 70) return [craftProb70, 80];
  return [craftProb99, 80];
}

function _calcranges(ranges) {
  const stats = {};
  for (let i = 0; i < ranges.length; ++i) {
    for (const id of ranges[i].stats) {
      stats[id] = stats[id] || [];
      stats[id].push(i);
    }
  }

  let result = 1;

  const visited = ranges.map(() => false);
  for (let i = 0; i < ranges.length; ++i) {
    if (visited[i]) continue;
    const values = {};
    const ridx = [];
    function dfs_stat(id) {
      for (const idx of stats[id]) {
        if (!visited[idx]) dfs_range(idx);
      }
    }
    function dfs_range(i) {
      visited[i] = true;
      ridx.push(i);
      for (const id of ranges[i].stats) {
        if (values[id] == null) {
          values[id] = ranges[i].value;
          dfs_stat(id);
        } else {
          values[id] += ranges[i].value;
        }
      }
    }
    dfs_range(i);

    function dfs_value(i) {
      const range = ranges[ridx[i]];
      if (i >= ridx.length - 1) {
        if (Object.keys(values).some(id => values[id] > 0 && !range.stats.includes(id))) return 0;
        const required = Math.max(...range.stats.map(id => values[id]), range.min);
        if (required > range.max) return 0;
        return (range.max - required + 1) / (range.max - range.min + 1);
      } else {
        let sum = 0;
        for (let value = range.min; value <= range.max; ++value) {
          for (const id of range.stats) values[id] -= value;
          sum += dfs_value(i + 1);
          for (const id of range.stats) values[id] += value;
        }
        return sum / (range.max - range.min + 1);
      }
    }
    result *= dfs_value(0);
  }

  return result;
}

class RangeCalculator {
  _mods = [];

  modsEx(mods, values, item) {
    const ranges = [[], [], [], []];
    const frequencies = [];
    for (const [, mod] of mods) {
      if (mod.frequency === 0) continue;
      frequencies.push(mod.frequency || 1);
      ranges[0].push([mod.mod1min || 0, mod.mod1max || 0]);
      ranges[1].push([mod.mod2min || 0, mod.mod2max || 0]);
      ranges[2].push([mod.mod3min || 0, mod.mod3max || 0]);
      ranges[3].push([mod.mod4min || 0, mod.mod4max || 0]);
    }
    const rangesValid = ranges.map((rng, i) => {
      if (!rng.length || values[i] !== (values[i] | 0)) return false;
      const min = Math.min(...rng.map(r => r[0]));
      const max = Math.max(...rng.map(r => r[1]));
      if (min >= max || min !== (min | 0) || max !== (max | 0)) return false;
      return true;
    });

    const stats = modsToStats({}, {[mods[0][0]]: rangesValid.map((rng, i) => rng ? `{${i}}` : 0)}, {base: item.base, ilvl: item.ilvl});

    const statIds = [];
    const allStats = [];

    for (const id in stats) {
      const m = stats[id].match?.(/^{(\d+)}$/);
      if (!m) continue;
      statIds[m[1]] = statIds[m[1]] || [];
      statIds[m[1]].push(id);
      allStats.push(id);
    }

    const statIdx = [0, 1, 2, 3].filter(i => statIds[i]);

    const totalFreq = frequencies.reduce((sum, x) => sum + x, 0);

    this._mods.push({
      stats: allStats,
      tiers: frequencies.map((freq, i) => ({
        frequency: freq / totalFreq,
        ranges: statIdx.map(si => ({
          stats: statIds[si],
          min: ranges[si][i][0],
          max: ranges[si][i][1],
          value: values[si],
        })),
      })),
    });
  }

  mods(mods, values, item) {
    this.modsEx(mods.map(id => [id, Data.mods[id]]), values, item);
  }

  calculate() {
    const mods = this._mods;
    const stats = {};
    for (let i = 0; i < mods.length; ++i) {
      for (const id of mods[i].stats) {
        stats[id] = stats[id] || [];
        stats[id].push(i);
      }
    }

    let result = 1;

    const visited = mods.map(() => false);
    for (let i = 0; i < mods.length; ++i) {
      if (visited[i]) continue;
      const statvis = {};
      const midx = [];
      function dfs_stat(id) {
        statvis[id] = true;
        for (const idx of stats[id]) {
          if (!visited[idx]) dfs_mod(idx);
        }
      }
      function dfs_mod(i) {
        visited[i] = true;
        midx.push(i);
        for (const id of mods[i].stats) {
          if (!statvis[id]) {
            dfs_stat(id);
          }
        }
      }
      dfs_mod(i);

      const ranges = [];
      function dfs_tier(i) {
        if (i >= midx.length) {
          return _calcranges(ranges);
        } else {
          const mod = mods[midx[i]];
          let total = 0;
          for (const t of mod.tiers) {
            const plen = ranges.length;
            ranges.push(...t.ranges);
            total += t.frequency * dfs_tier(i + 1);
            ranges.length = plen;
          }
          return total;
        }
      }
      result *= dfs_tier(0);
    }

    return result;
  }
}

function affixProbability(item, modGroups, ilvl, calc) {
  const alvl = itemAffixLevel(item.base, item.ilvl);
  const groupP = processGroups(modGroups.prefix, alvl);
  const groupS = processGroups(modGroups.suffix, alvl);
  const itemPid = Object.keys(item.mods).filter(id => Data.magicPrefix[id] && item.mods[id]);
  const itemP = itemPid.map(id => groupP.findIndex(g => g.ids.has(id)));
  const itemSid = Object.keys(item.mods).filter(id => Data.magicSuffix[id] && item.mods[id]);
  const itemS = itemSid.map(id => groupS.findIndex(g => g.ids.has(id)));
  if (itemP.some(idx => idx < 0) || itemS.some(idx => idx < 0)) return 0;
  const maxMod = item.quality === Quality.MAGIC ? 1 : 3;
  if (itemP.length > maxMod || itemS.length > maxMod) return 0;

  const pickP = pickFrequency(groupP.map(g => g.frequency), itemP, item.quality === Quality.MAGIC ? 1 : 3);
  const pickS = pickFrequency(groupS.map(g => g.frequency), itemS, item.quality === Quality.MAGIC ? 1 : 3);

  const [probN, probD] = probTable(item.quality, ilvl);
  let total = 0;
  for (let P = itemP.length; P <= maxMod; ++P) {
    for (let S = itemS.length; S <= maxMod; ++S) {
      total += pickP[P] * pickS[S] * probN[P][S];
    }
  }

  itemPid.forEach((id, index) => {
    const group = groupP[itemP[index]].groups.find(g => g.mods.includes(id));
    total *= group.frequency / groupP[itemP[index]].frequency;
    calc.mods(group.mods, item.mods[id], item);
  });
  itemSid.forEach((id, index) => {
    const group = groupS[itemS[index]].groups.find(g => g.mods.includes(id));
    total *= group.frequency / groupS[itemS[index]].frequency;
    calc.mods(group.mods, item.mods[id], item);
  });

  return total / probD;
}

function singleProbability(item, stats, groups, ilvl, calc) {
  const group = processGroups(groups, ilvl);
  const itemAid = Object.keys(stats)[0];
  if (itemAid == null || !stats[itemAid]) return 1;
  const itemA = group.findIndex(g => g.ids.has(itemAid));
  if (itemA < 0) return 0;

  const subGroup = group[itemA].groups.find(g => g.mods.includes(itemAid));
  calc.mods(subGroup.mods, stats[itemAid], item);

  return pickFrequency(group.map(g => g.frequency), [itemA], 1)[1] * subGroup.frequency / group[itemA].frequency;
}

function craftProbability(item, modGroups, ilvl, calc) {
  const itemCid = Object.keys(item.crafted)[0];
  if (itemCid == null || !item.crafted[itemCid]) return 1;
  const group = modGroups.crafted.find(g => g.mods.includes(itemCid));
  if (!group) return 0;

  calc.mods(group.mods, item.crafted[itemCid], item);

  return 1;
}

function staffProbability(item, modGroups, ilvl, calc, imbue) {
  let baseTier = 1;
  if (ilvl >= 37 && GameVersion === 100) baseTier = 5;
  else if (ilvl >= 25) baseTier = 4;
  else if (ilvl >= 19) baseTier = 3;
  else if (ilvl >= 12) baseTier = 2;

  const tierOdds = [0, 0, 0, 0, 0, 0, 0];
  const maxTier = (item.quality === Quality.LOW ? 4 : 6);
  tierOdds[Math.max(1, baseTier - 2)] += 1;
  tierOdds[Math.max(1, baseTier - 1)] += 2;
  tierOdds[Math.min(maxTier, baseTier)] += 5;
  tierOdds[Math.min(maxTier, baseTier + 1)] += 2;

  const valueBonus = (imbue ? (ilvl >> 1) : 0);
  const rangeProb = (min, max, bonus) => Math.max(0, Math.min(max, bonus + 99) - Math.max(min, bonus) + 1);
  const valueProb = [
    rangeProb(0, 59, valueBonus),
    rangeProb(60, 89, valueBonus),
    rangeProb(90, 200, valueBonus),
  ];

  const types = itemGetTypes(item.base);

  const groups = [], freq = [], ids = [], groupr = [], groupt = [];
  const total = [0, 0, 0, 0, 0, 0, 0];
  const totalR = [0, 0, 0, 0, 0, 0, 0];
  for (const g of modGroups.staff) {
    const id = g.mods[0];
    const mod = Data.staffMods[id];
    const skill = Data.skills[mod.mod1param];
    if (!skill) continue;

    const tier = Math.floor(mod.tier / 6) + 1;
    if (!tierOdds[tier]) continue;

    const restricted = skill.itypea1 && !types.has(skill.itypea1);
    groupt.push(tier);
    groupr.push(restricted);
    ids.push(id);
    freq.push(tierOdds[tier]);
    total[tier] += 1;
    if (restricted) totalR[tier] += 1;

    const mods = [];
    for (let i = 0; i < 3; ++i) {
      if (valueProb[0]) {
        mods.push([id, {
          mod1min: i + 1,
          mod1max: i + 1,
          frequency: valueProb[i],
        }]);
      }
    }
    groups.push(mods);
  }
  for (let t = 1; t <= 6; ++t) {
    if (!totalR[t]) continue;
    const rp = totalR[t] / total[t];
    const rp5 = Math.pow(rp, 5);
    const multR = rp5;
    const multO = (1 - rp * rp5) / (1 - rp);
    for (let i = 0; i < groups.length; ++i) {
      if (groupt[i] === t) {
        freq[i] *= (groupr[i] ? multR : multO);
      }
    }
  }

  const itemSid = Object.keys(item.staff).filter(id => item.staff[id]);
  const itemS = itemSid.map(id => ids.indexOf(id));
  if (itemS.some(idx => idx < 0)) return 0;

  itemSid.forEach((id, index) => calc.modsEx(groups[itemS[index]], item.staff[id], item));

  const pickS = pickFrequency(freq, itemS, 3);
  const countBonus = (imbue ? ilvl : 0);
  return [[0, 30], [31, 70], [71, 90], [91, 200]].reduce((sum, [min, max], i) => sum + rangeProb(min, max, countBonus) * pickS[i] / 100, 0);
}

function uniqueProbability(item) {
  const unique = (Data.uniqueItems[item.unique] || Data.setItems[item.unique] || Data.runes[item.unique]);
  const isRuneword = (item.quality === Quality.RUNEWORD);
  if (!unique) return null;

  const ranges = [];
  if (isRuneword) {
    for (let i = 1; i <= 7; ++i) {
      if (unique[`t1code${i}`]) {
        if (unique[`t1min${i}`] >= unique[`t1max${i}`]) {
          ranges[i - 1] = unique[`t1max${i}`];
        } else {
          ranges[i - 1] = [unique[`t1min${i}`], unique[`t1max${i}`]];
        }
      }
    }
  } else {
    for (let i = 1; i <= 12; ++i) {
      if (unique[`prop${i}`]) {
        if (unique[`min${i}`] >= unique[`max${i}`]) {
          ranges[i - 1] = unique[`max${i}`];
        } else {
          ranges[i - 1] = [unique[`min${i}`], unique[`max${i}`]];
        }
      }
    }
  }

  const values = ranges.map((rng, i) => Array.isArray(rng) ? `{${i}}` : rng);
  const stats = (isRuneword ? runewordStats : uniqueStats)({}, unique, values, {base: item.base, ilvl: item.ilvl});
  const lines = statsToLines(stats).map(line => line[1]);

  let modP = 1;
  let valueP = 1;
  const used = [];

  for (const line of lines) {
    const regex = /{(\d)(?:;(\w+))?}/g;
    let match;
    while ((match = regex.exec(line))) {
      const index = match[1];
      const func = match[2];
      if (used[index]) continue;
      used[index] = true;
      const rng = ranges[index];
      if (!Array.isArray(rng)) continue;
      const prob = 1 / (rng[1] - rng[0] + 1);
      if (func === 'skill' || func === 'skillclass' || func === 'classskills') {
        modP *= prob;
      } else {
        valueP *= (rng[1] - item.uniqueValues[index] + 1) * prob;
      }
    }
  }

  return {mods: modP, values: valueP};
}

export default function itemProbability(item, modGroups, imbue) {
  if (item.custom) return;
  if (item.quality === Quality.UNIQUE || item.quality === Quality.SET || item.quality === Quality.RUNEWORD) {
    return uniqueProbability(item);
  }

  const ilvl = item.ilvl || 99;
  let modP = 1;
  const calc = new RangeCalculator();
  if (item.mods && modGroups.prefix && modGroups.suffix && [Quality.MAGIC, Quality.RARE, Quality.CRAFTED].includes(item.quality)) {
    modP *= affixProbability(item, modGroups, ilvl, calc);
  }
  if (item.auto && modGroups.auto && item.quality !== Quality.RUNEWORD) {
    modP *= singleProbability(item, item.auto, modGroups.auto, ilvl, calc);
  }
  if (item.superior && modGroups.superior && item.quality !== Quality.RUNEWORD) {
    modP *= singleProbability(item, item.superior, modGroups.superior, ilvl, calc);
  }
  if (item.crafted && modGroups.crafted && item.quality === Quality.CRAFTED) {
    modP *= craftProbability(item, modGroups, ilvl, calc);
  }
  if (item.staff && modGroups.staff && item.quality !== Quality.RUNEWORD) {
    modP *= staffProbability(item, modGroups, ilvl, calc, imbue);
  }
  return {mods: modP, values: calc.calculate() * modP};
}
